const SCRIPT_URL_POLICY_NAME = "chateau-syam";
const DEFAULT_HTML_POLICY_NAME = "default";
const REACT_SCRIPT_BOOTSTRAP = "<script></script>";
const ALLOWED_SCRIPT_ORIGINS = new Set([
  "https://login.smoobu.com",
  "https://maps.googleapis.com",
]);

type TrustedTypesPolicy = {
  createScriptURL?: (value: string) => unknown;
  createHTML?: (value: string) => unknown;
  createScript?: (value: string) => unknown;
};

type TrustedTypesFactory = {
  createPolicy: (name: string, rules: { createScriptURL?: (value: string) => string; createHTML?: (value: string) => string; createScript?: (value: string) => string }) => TrustedTypesPolicy;
  getPolicy?: (name: string) => TrustedTypesPolicy | null;
};

let scriptUrlPolicy: TrustedTypesPolicy | null = null;

function getTrustedTypesFactory() {
  return (window as Window & { trustedTypes?: TrustedTypesFactory }).trustedTypes;
}

export function initializeTrustedTypes() {
  const trustedTypes = getTrustedTypesFactory();
  if (!trustedTypes || scriptUrlPolicy) return;

  if (!trustedTypes.getPolicy?.(DEFAULT_HTML_POLICY_NAME)) {
    trustedTypes.createPolicy(DEFAULT_HTML_POLICY_NAME, {
      // React crée un nœud script temporaire via innerHTML pour initialiser ses éléments <script>.
      // La politique n’accepte que cette constante interne ; toute autre écriture HTML est rejetée.
      createHTML(value) {
        if (value !== REACT_SCRIPT_BOOTSTRAP) {
          throw new TypeError("Écriture TrustedHTML non autorisée");
        }
        return value;
      },
      // Les seules données écrites dans une balise script par l’application sont les JSON-LD sérialisés.
      // Le JSON est validé avant de recevoir TrustedScript : aucun JavaScript arbitraire n’est autorisé.
      createScript(value) {
        try {
          JSON.parse(value);
          return value;
        } catch {
          throw new TypeError("Écriture TrustedScript non autorisée");
        }
      },
    });
  }

  scriptUrlPolicy = trustedTypes.getPolicy?.(SCRIPT_URL_POLICY_NAME) ?? trustedTypes.createPolicy(SCRIPT_URL_POLICY_NAME, {
    createScriptURL(value) {
      const url = new URL(value, window.location.origin);
      if (!ALLOWED_SCRIPT_ORIGINS.has(url.origin)) {
        throw new TypeError(`Origine de script non autorisée : ${url.origin}`);
      }
      return url.href;
    },
  });
}

export function setTrustedScriptSource(script: HTMLScriptElement, value: string) {
  initializeTrustedTypes();
  script.src = (scriptUrlPolicy?.createScriptURL ? scriptUrlPolicy.createScriptURL(value) : value) as string;
}
